'use client';

import { Controller, useFormContext } from 'react-hook-form';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import useTranslations from '@/hooks/useTranslations';

export type TriState = 'unchanged' | 'true' | 'false';

/** unchanged → yes → no → unchanged */
const NEXT_STATE: Record<TriState, TriState> = {
  unchanged: 'true',
  true: 'false',
  false: 'unchanged',
};

export const toBoolean = (value: unknown): boolean | undefined =>
  value === 'true' ? true : value === 'false' ? false : undefined;

export default function TriStateCheckbox({
  name,
  label,
  description,
}: {
  name: string;
  label: string;
  description?: string;
}) {
  const { t } = useTranslations();
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const value: TriState =
          field.value === 'true' || field.value === 'false' ? field.value : 'unchanged';

        return (
          <Stack sx={{ px: 0.5, py: 0.5, display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              label={label}
              control={
                <Checkbox
                  checked={value === 'true'}
                  indeterminate={value === 'unchanged'}
                  onChange={() => field.onChange(NEXT_STATE[value])}
                  onBlur={field.onBlur}
                  slotProps={{ input: { ref: field.ref } }}
                />
              }
            />
            {(description || value === 'unchanged') && (
              <Typography variant='caption' color='text.secondary'>
                {[
                  description,
                  value === 'unchanged' && `(${t('dataGrid.bulkActions.credits.unchanged')})`,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </Typography>
            )}
          </Stack>
        );
      }}
    />
  );
}
